import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { colors, margins } from "style";
import { LEGACY_TERMSHEET_COLUMNS_CONFIG } from "constants/index";
import { TableHead, TableRow, TableCell, TableBody } from "components/Table";
import {
  AddResourceContainer,
  LegacyCard,
  LegacyHeaderCell,
  LegacyLoadingTable,
  LegacyTable,
  LegacyTableWrapper,
  LegacyTermSheetTableHeader,
  LegacyTermSheetTableRow,
  TotalsRow,
} from "components/TermSheets";
import { ProfilesPicker } from "components/Profiles";
import Icon, { ICON_TYPE, ICON_SIZE } from "components/Icon";
import { PrimaryLink } from "components/Links";
import { useLegacyTermSheet, useTermSheetContext } from "hooks";

/**
 * LegacyTermSheetTable
 * Displays termsheet data in a table format with editable cells
 * Uses Context and custom hooks for state management
 *
 * @param {boolean} loading - Whether the data is still loading
 */
const LegacyTermSheetTable = ({ loading, ...props }) => {
  if (loading) return <LegacyLoadingTable {...props} />;

  const { termSheet, buyInCurrency, sellInCurrency, isDraft, isInactive } = useTermSheetContext();
  const { handleAddMembersToTermSheet, addingMembers, handleRemoveMemberFromTermSheet } = useLegacyTermSheet();

  const [showProfilesPicker, setShowProfilesPicker] = useState(false);
  const [selectedProfileIds, setSelectedProfileIds] = useState([]);
  const [isExpanded, setIsExpanded] = useState(!isInactive);

  const localAssignments = termSheet?.assignments || [];

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  // Sync expansion state when termsheet state changes
  useEffect(() => {
    setIsExpanded(!isInactive);
  }, [isInactive]);

  // Handle batch adding members (from ProfilesPicker)
  const handleAddMembers = async () => {
    await handleAddMembersToTermSheet(termSheet, selectedProfileIds);
    setShowProfilesPicker(false);
    setSelectedProfileIds([]);
  };

  // Handle removing a member
  const handleRemoveMember = (assignment) => handleRemoveMemberFromTermSheet(termSheet, assignment);

  return (
    <LegacyCard {...props}>
      <LegacyTermSheetTableHeader loading={loading} isExpanded={isExpanded} onToggle={toggleExpanded} {...props} />
      {isExpanded && (
        <LegacyTableWrapper>
          <LegacyTable layout="auto">
            <TableHead>
              <TableRow>
                {LEGACY_TERMSHEET_COLUMNS_CONFIG.map(({ key, width, label, highlightColor }) => (
                  <LegacyHeaderCell
                    key={key}
                    scope="col"
                    width={width}
                    marginSize={margins.normal}
                    highlightColor={highlightColor}
                  >
                    {typeof label === "function" ? label({ buyInCurrency, sellInCurrency }) : label}
                  </LegacyHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && localAssignments?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={LEGACY_TERMSHEET_COLUMNS_CONFIG.length}>
                    <EmptyMessage>
                      <FormattedMessage id="TermSheets.LegacyTermSheetTable.NoAssignments" />
                    </EmptyMessage>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                localAssignments?.map((assignment) => (
                  <LegacyTermSheetTableRow
                    key={assignment.id || assignment.profile?.uuid}
                    assignment={assignment}
                    buyInCurrency={buyInCurrency}
                    sellInCurrency={sellInCurrency}
                    onRemoveMember={handleRemoveMember}
                    {...props}
                  />
                ))}
              {!loading && localAssignments?.length > 0 && <TotalsRow />}
            </TableBody>
          </LegacyTable>
        </LegacyTableWrapper>
      )}
      {isDraft && isExpanded && !loading && (
        <AddResourceContainer>
          <PrimaryLink onClick={() => setShowProfilesPicker(true)}>
            <Icon color={colors.purpleRainBase} type={ICON_TYPE.plus} size={ICON_SIZE.medium} />
            <FormattedMessage id="TermSheets.LegacyTermSheetTable.AddMember" />
          </PrimaryLink>
        </AddResourceContainer>
      )}
      {isDraft && (
        <ProfilesPicker
          show={showProfilesPicker}
          onClose={() => {
            setShowProfilesPicker(false);
            setSelectedProfileIds([]);
          }}
          title={<FormattedMessage id="Profiles.ProfilesPicker.Title" />}
          info={<FormattedMessage id="Profiles.ProfilesPicker.Description" />}
          loading={addingMembers}
          onSelect={setSelectedProfileIds}
          onSubmit={handleAddMembers}
          variables={{
            filters: {
              type: "MEMBER",
              excludeIds: localAssignments?.map(({ profile }) => profile?.uuid),
            },
          }}
          selectable
          showSave
          canSubmit
        />
      )}
    </LegacyCard>
  );
};

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${margins.large};
  color: ${colors.grayAnatomyDark2};
`;

LegacyTermSheetTable.propTypes = {
  loading: PropTypes.bool,
};

export default LegacyTermSheetTable;
